import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { apiClient } from "../../base_url/config"
import { clearTokenAfterPayment } from "../auth/authSlice"

// Request interface
export interface CreatePaymentRequest {
    type: "order"
    order_id: number | string
    platform: "qr"
}

// Response interface
export interface CreatePaymentResponse {
    message: string
    data: {
        client_secret: string
    }
}

// State interface
interface CreatePaymentState {
    isLoading: boolean
    error: string | null
    clientSecret: string | null
    lastOrderId: number | string | null
}

const initialState: CreatePaymentState = {
    isLoading: false,
    error: null,
    clientSecret: null,
    lastOrderId: null,
}

// Async thunk for creating payment
export const createPayment = createAsyncThunk(
    "createPayment/createPayment",
    async (
        { order_id, token, lang }: {
            order_id: number | string
            token: string
            lang: string
        },
        { rejectWithValue }
    ) => {
        try {
            const paymentData: CreatePaymentRequest = {
                type: "order",
                order_id: order_id,
                platform: "qr"
            }

            const response = await apiClient.post<CreatePaymentResponse>(
                "/qr/paymob/createPayment",
                paymentData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        // lang will be added automatically by interceptor
                    },
                }
            )

            return {
                clientSecret: response.data.data.client_secret,
                message: response.data.message,
                orderId: order_id,
            }
        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || "Payment creation failed"
            return rejectWithValue(errorMessage)
        }
    }
)

// Async thunk for completing payment and clearing token
export const completePaymentAndClearToken = createAsyncThunk(
    "createPayment/completePaymentAndClearToken",
    async (_, { dispatch }) => {
                try {
            // Clear the authentication token to allow new QR code usage
            dispatch(clearTokenAfterPayment())

            // Reset payment state
            dispatch(resetPaymentState())

            return { success: true, message: 'Payment completed and token cleared' }
        } catch (error: any) {
            throw error
        }
    }
)

const createPaymentSlice = createSlice({
    name: "createPayment",
    initialState,
    reducers: {
        clearPaymentError: (state) => {
            state.error = null
        },
        clearPaymentData: (state) => {
            state.clientSecret = null
            state.lastOrderId = null
            state.error = null
        },
        resetPaymentState: (state) => {
            state.isLoading = false
            state.error = null
            state.clientSecret = null
            state.lastOrderId = null
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createPayment.pending, (state) => {
                state.isLoading = true
                state.error = null
            })
            .addCase(createPayment.fulfilled, (state, action) => {
                state.isLoading = false
                state.clientSecret = action.payload.clientSecret
                state.lastOrderId = action.payload.orderId
                state.error = null
            })
            .addCase(createPayment.rejected, (state, action) => {
                state.isLoading = false
                state.error = action.payload as string
                state.clientSecret = null
            })
    },
})

export const { clearPaymentError, clearPaymentData, resetPaymentState } = createPaymentSlice.actions

// Selectors
export const selectCreatePayment = (state: { createPayment: CreatePaymentState }) => state.createPayment
export const selectPaymentLoading = (state: { createPayment: CreatePaymentState }) => state.createPayment.isLoading
export const selectPaymentError = (state: { createPayment: CreatePaymentState }) => state.createPayment.error
export const selectClientSecret = (state: { createPayment: CreatePaymentState }) => state.createPayment.clientSecret
export const selectLastOrderId = (state: { createPayment: CreatePaymentState }) => state.createPayment.lastOrderId

export default createPaymentSlice.reducer